import { take } from "rxjs/operators";
import { WebsocketService } from "./services/api/websocket.service";
import { switchMap, mergeMap } from "rxjs/operators";
import { Component, OnInit, OnDestroy, Renderer2, inject } from "@angular/core";
import {
	Platform,
	LoadingController,
	IonicModule,
	AlertController,
	ModalController,
} from "@ionic/angular";
import { StatusBar } from "@capacitor/status-bar";
import { NavigationEnd, Router } from "@angular/router";
import { ApiService } from "./services/api/api.service";
import { TranslateService } from "@ngx-translate/core";
import { LanguageService } from "./services/language-service/language.service";
import { LicenseService } from "./services/api/license.service";
import { filter, of, Subscription, timer } from "rxjs";
import { ModalService } from "./services/modal-service/modal.service";
import { ResourceService } from "./services/resources/resource.service";
import { AuthGuard } from "./guards/auth.guard";
import { SplashScreen } from "@capacitor/splash-screen";
import { App } from "@capacitor/app";
import { CommonModule } from "@angular/common";
import { VariantService } from "src/app/services/variant-service/variant.service";
import { NetworkserviceService } from "./services/Networkservice/networkservice.service";
import { Network } from "@capacitor/network";
import { Led } from "@nettask/hxa-capacitor-led-plugin";
import { ModalTypes } from "./services/modal-service/modal-types";
import { ScreenSaverService } from "./components/screen-saver/screen-saver.service";
import { ScreenSaverLeftContentComponent } from "./components/screen-saver-content/screen-saver-left-content.component";
import { ScreenSaverRightContentComponent } from "./components/screen-saver-content/screen-saver-right-content.component";
import { KioskService } from "./services/hxa-device-services/kiosk.service";

@Component({
	selector: "app-root",
	templateUrl: "app.component.html",
	imports: [IonicModule, CommonModule],
})
export class AppComponent implements OnInit, OnDestroy {
	appVersion = "2.3.0";
	private reconnectRunning = false;
	public licenseSubscription: Subscription;
	public loading: boolean = false;
	private resourceSubscription: Subscription;

	private readonly kioskService = inject(KioskService);

	constructor(
		private platform: Platform,
		private router: Router,
		private api: ApiService,
		private licenseService: LicenseService,
		public translate: TranslateService,
		private language: LanguageService,
		private socket: WebsocketService,
		private modalService: ModalService,
		private resourceService: ResourceService,
		public authGuard: AuthGuard,
		private translationService: TranslateService,
		private NetworkService: NetworkserviceService,
		private alertController: AlertController,
		private screenSaverService: ScreenSaverService,
		private variantService: VariantService,
	) {
		this.initializeApp();
		this.authGuard.loader$.subscribe((loading) => {
			this.loading = loading as boolean;
		});
	}

	ngOnInit() {
		if (this.variantService.isHxaDevices) {
			Led.setLedOn();
			setTimeout(() => {
				Led.setLedWhite();
			}, 750);
		}

		(window as any).onShowInformationPageKeyComboDetected = async (success: boolean) => {
			let wifiConnection = await this.checkNetworkType();
			if (success && this.router.url == "/login") {
				const localIp = await this.NetworkService.getLocalIPAddress();
				await this.showAlert(localIp, wifiConnection);
			}

			if (success && this.router.url == "/dashboard") {
				const opts = ModalTypes.informationPage();
				this.modalService.showModal(opts);
			}
		};

		this.initScreenSaver();
		this.setupResourceListener();
	}

	ngOnDestroy() {
		this.resourceSubscription?.unsubscribe();
	}

	private initScreenSaver() {
		this.screenSaverService.init({
			leftComponent: ScreenSaverLeftContentComponent,
			rightComponent: ScreenSaverRightContentComponent,
			activeOnRoutes: ["/dashboard"],
			isEnabled: () =>
				this.resourceService.resource?.value?.settings?.screenSaver?.isActive ?? false,
			getInactivityDuration: () =>
				this.resourceService.resource?.value?.settings?.screenSaver?.duration ?? 60,
			shouldNotOpenWhen: async () => !!(await this.modalService.isModalOpen()),
		});
	}

	private setupResourceListener() {
		this.resourceSubscription = this.resourceService.resource.subscribe((resource) => {
			if (!resource?.settings?.screenSaver?.isActive) {
				this.screenSaverService.stop();
			} else {
				this.screenSaverService.resetInactivityTimer();
			}
		});
	}
	async checkNetworkType() {
		const status = await Network.getStatus();
		if (status.connectionType === "wifi") {
			const { Wifi } = await import("@codext/capacitor-wifi");
			let wifi = await Wifi.getCurrentWifi();
			const ssid = wifi.currentWifi.ssid;
			if (ssid) return ssid;
			return null;
		} else if (status.connectionType === "unknown") {
			return this.translationService.instant("Ethernet_Connection");
		} else {
			return this.translationService.instant("Not_Connected");
		}
	}
	async showAlert(localIp: string, wifiConnection: any) {
		const wifiDisplay = wifiConnection || this.translationService.instant("NONE");
		const alert = await this.alertController.create({
			header: this.translationService.instant("DEVICE_INFORMATION"),
			message: `Wi-Fi: ${wifiDisplay} <br> Local IP Address: ${localIp}`,
			buttons: ["OK"],
		});

		await alert.present();
	}

	initializeApp() {
		this.platform.ready().then(async () => {
			StatusBar.hide();
			setTimeout(() => {
				SplashScreen.hide();
			}, 1000);
			if (this.platform.is("cordova")) {
				const version = (await App.getInfo()).version;
				if (version !== null) {
					this.api.softwareVersion.next(version);
				} else {
					this.api.softwareVersion.next(this.appVersion);
				}
			} else {
				this.api.softwareVersion.next(this.appVersion);
			}
			this.language.setInitLang();

			/*Start resource listening changes*/
			this.resourceService.startResourceListener().subscribe((res: any) => {
				if (res) {
					if (this.licenseService.license.getValue() != null) {
						this.licenseService
							.checkingLicense(this.licenseService.license.getValue())
							.toPromise();
					}
				}
			});

			this.licenseSubscription = this.licenseService.licenseListener().subscribe();

			/*Remove Connector Callback*/
			// this.socket.removeConnectorCallback.subscribe((r) => {
			// 	if (r) {
			// 		this.socket.connectionCloseWithReconnect();
			// 	}
			// });

			/*Reconnect Socket if undefined status of connection */
			this.socket.socketStatus
				.pipe(
					mergeMap(() => {
						if (!this.reconnectRunning) {
							this.reconnectRunning = true;
							let time = Math.floor(Math.random() * (60 - 5 + 1) + 5);
							time = time * 1000;

							return timer(time).pipe(
								switchMap(() => {
									this.reconnectRunning = false;

									this.api
										.readDeviceLicense()
										.pipe(take(1))
										.subscribe((li) => {
											this.licenseService.checkingLicense(li).toPromise();
										});

									return this.api.createSocketConnection();
								}),
							);
						}
						return of(true);
					}),
				)
				.subscribe();

			/*Wipe device Callback */
			this.removeDeviceListener();
			this.maintenanceModeListener();

			await this.api.getDeviceInfo();

			if (this.variantService.isHxaDevices) {
				this.kioskService.initialize();
			}
		});

		this.api.reConnectingStatus$.subscribe({
			next: (connected) => {},
		});
	}

	// async showloading() {
	// 	this.loadingComponent = await this.loadingCtrl.create({
	// 		message: this.translationService.instant("RECONNECTING"),
	// 	});
	// 	this.loadingComponent.present();
	// }
	// async dismissloading() {
	// 	if (this.loadingComponent) {
	// 		this.loadingComponent.dismiss();
	// 	}
	// }

	removeDeviceListener() {
		this.socket.removeCallback.subscribe(() => {
			this.removeDevice();
		});
	}

	maintenanceModeListener() {
		this.socket.maintenanceModeCallback.subscribe(async (isMaintenance) => {
			if (isMaintenance) {
				await this.kioskService.disable();
			}
		});
	}

	async removeDevice() {
		this.licenseSubscription.unsubscribe();
		this.socket.connectionClose();
		this.resourceService.resource.next(undefined);
		this.modalService.isModalOpen().then((v) =>
			v
				? this.modalService
						.dismissModal()
						.then()
						.catch((_) => {})
				: null,
		);
		this.api.authenticationState.next(false);

		let serial;
		this.api.getSerial().then((s) => {
			serial = s;
		});

		await this.api.clearStorage().then(() => {
			this.api.saveSerial(serial);
			this.api.deviceId.next(undefined);
			this.router.navigateByUrl("/login");
		});
	}
}
